export interface Country {
  code: string;
  name: string;
  /** "in der Schweiz", for texts */
  inCountry: string;
  /** Example VAT ID, shown as placeholder/hint for the UST-ID field. */
  vatIdExample: string;
  /**
   * Whether the fee invoice needs the company's UST-ID: Schenkwerk invoices from
   * Germany, so other EU countries are cross-border B2B (reverse charge). Outside
   * the EU (Switzerland) the service is not taxable in Germany and needs none.
   */
  vatIdRequired: boolean;
  /** May run or join a network (networks settle by SEPA transfer in euro). */
  supportsNetworks: boolean;
  /**
   * Where a voucher without a printed date runs longer than one with it: the rule,
   * for the hint in the voucher design settings. The years come from the backend.
   */
  validityWithoutDate?: string;
}

/**
 * Countries a company can be based in. Must stay in sync with COUNTRIES in the
 * backend (src/backend/countries.py) — the backend rejects anything else. The
 * currency is not listed here: the backend sends it with the company.
 */
export const COUNTRIES: Country[] = [
  {
    code: 'DE',
    name: 'Deutschland',
    inCountry: 'in Deutschland',
    vatIdExample: 'DE123456789',
    vatIdRequired: false,
    supportsNetworks: true,
  },
  {
    code: 'AT',
    name: 'Österreich',
    inCountry: 'in Österreich',
    vatIdExample: 'ATU12345678',
    vatIdRequired: true,
    supportsNetworks: true,
  },
  {
    code: 'IT',
    name: 'Italien',
    inCountry: 'in Italien',
    vatIdExample: 'IT12345678901',
    vatIdRequired: true,
    supportsNetworks: true,
    validityWithoutDate:
      'In Italien läuft ein Gutschein ohne aufgedrucktes Datum bis zur gesetzlichen ' +
      'Verjährung (Art. 2946 Codice Civile).',
  },
  {
    code: 'CH',
    name: 'Schweiz',
    inCountry: 'in der Schweiz',
    vatIdExample: 'CHE-123.456.789 MWST',
    vatIdRequired: false,
    // Swiss vouchers run 10 years (Art. 127 OR) with a printed date too: no hint
    supportsNetworks: false,
  },
];

export const DEFAULT_COUNTRY_CODE = 'DE';

export function getCountry(code: string | null | undefined): Country | undefined {
  return code ? COUNTRIES.find((c) => c.code === code) : undefined;
}

export function getCountryName(code: string | null | undefined): string {
  if (!code) return '';
  return getCountry(code)?.name ?? code;
}

/** Whether a company billed in this country needs a UST-ID on its invoices. */
export function requiresVatId(code: string | null | undefined): boolean {
  return !!getCountry(code)?.vatIdRequired;
}

/** Whether a company billed in this country may run or join a network. */
export function supportsNetworks(code: string | null | undefined): boolean {
  return getCountry(code ?? DEFAULT_COUNTRY_CODE)?.supportsNetworks ?? true;
}
